import cx from "classnames";
import { Icon } from "common/Icon";
import { ColWrap, MainCol, SecondaryCol } from "common/layouts/SideNavLayout/TwoCol";
import { StandardLayout } from "common/layouts/StandardLayout";
import { Card } from "common/styleguide/Card";
import { CTA } from "common/styleguide/CTA";
import { Set } from "immutable";
import { Msg } from "lib/localization";
import { interfaceBase } from "lib/styleguide/base.scss";
import React, { useState } from "react";
import { defineMessages, FormattedMessage as T } from "react-intl";
import styles from "./Faq.scss";
import helpImg from "./images/help-center.jpg";
import helpImg2x from "./images/help-center@2x.jpg";
import helpImg3x from "./images/help-center@3x.jpg";

const messages = defineMessages({
  contactUs: {
    id: "faq.contactUs",
    defaultMessage: "Contact us",
  },
});

const faqItems = [
  [
    <T id="faq.gettingInTouchQ" defaultMessage="How do I get in touch with customer support?" />,
    <p>
      <T
        id="faq.gettingInTouchA"
        defaultMessage="Our customer support department is available seven days a week, providing support to users in English. To reach us, email <a>info@ridewithgps.com</a> and we will get back to you as soon as possible."
        values={{
          a: (...chunks) => <a href="mailto:info@ridewithgps.com">{chunks}</a>,
        }}
      />
    </p>,
  ],
  [
    <T id="faq.forgotPasswordQ" defaultMessage="I forgot my password. How do I retrieve it?" />,
    <>
      <p>
        <T
          id="faq.forgotPasswordA1"
          defaultMessage="If using your computer, go to <a>ridewithgps.com</a>. Click <strong>Log In</strong> in the upper right of the homepage and then click <strong>Forgot Password</strong>. Enter your email address and we will send instructions on setting a new password."
          values={{
            a: (...chunks) => <a href="/">{chunks}</a>,
            strong: (...chunks) => <strong>{chunks}</strong>,
          }}
        />
      </p>
      <p>
        <T
          id="faq.forgotPasswordA2"
          defaultMessage="If using the Ride with GPS app, open the app, tap <strong>I have an account</strong> and then <strong>Forgot Password</strong>.  Enter your email address and we will send instructions on setting a new password."
          values={{
            strong: (...chunks) => <strong>{chunks}</strong>,
          }}
        />
      </p>
    </>,
  ],
  [
    <T id="faq.managingSettingsQ" defaultMessage="How do I manage my account settings?" />,
    <>
      <p>
        <T
          id="faq.managingSettingsA1"
          defaultMessage="When using your computer, go to <a>ridewithgps.com</a>, click on your profile icon in the upper right of the homepage and then select <strong>Settings</strong>."
          values={{
            a: (...chunks) => <a href="/">{chunks}</a>,
            strong: (...chunks) => <strong>{chunks}</strong>,
          }}
        />
      </p>
      <p>
        <T
          id="faq.managingSettingsA2"
          defaultMessage="When using the Ride with GPS app, open the app and tap the cog icon in the upper left corner."
        />
      </p>
    </>,
  ],
  [
    <T id="faq.upgradeQ" defaultMessage="What comes with an Upgraded plan?" />,
    <>
      <p>
        <T
          id="faq.upgradeA"
          defaultMessage="We offer three types of subscription plans: Starter, Basic, and Premium. Full descriptions and a comparison of all three can be found <a>here</a>."
          values={{
            a: (...chunks) => <a href="/plans#compare">{chunks}</a>,
          }}
        />
      </p>
    </>,
  ],
  [
    <T id="faq.cancelSubscriptionQ" defaultMessage="How do I cancel my Subscription?" />,
    <>
      <p>
        <T
          id="faq.cancelSubscriptionAWeb"
          defaultMessage="If you purchased your subscription through Ride with GPS Web Billing: Log into your account on <a>ridewithgps.com</a>, click on your profile icon, then select <strong>Manage Subscription</strong>, and select <strong>FREE</strong> from the dropdown subscription menu to cancel your subscription."
          values={{
            a: (...chunks) => <a href="/">{chunks}</a>,
            strong: (...chunks) => <strong>{chunks}</strong>,
          }}
        />
      </p>
      <p>
        <T
          id="faq.cancelSubscriptionAApp"
          defaultMessage="If you purchased your subscription through iTunes or GooglePlay: Open the app and tap <strong>More</strong> in the lower right corner. Select <strong>Manage Subscription</strong>, tap on the link to your billing provider, and then tap on the Ride with GPS icon to manage your subscription."
          values={{
            strong: (...chunks) => <strong>{chunks}</strong>,
          }}
        />
      </p>
    </>,
  ],
  [
    <T id="faq.getRefundQ" defaultMessage="How do I get a refund?" />,
    <>
      <p>
        <T
          id="faq.getRefundAWeb"
          defaultMessage="If you purchased your subscription through Ride with GPS Web Billing or GooglePlay: Contact our customer support department at <a>info@ridewithgps.com</a> and we will be glad to assist you."
          values={{
            a: (...chunks) => <a href="mailto:info@ridewithgps.com">{chunks}</a>,
          }}
        />
      </p>
      <p>
        <T
          id="faq.getRefundAApple"
          defaultMessage="If you purchased your subscription through iTunes: Due to licensing rules, you must go directly through <a>iTunes customer support</a> to request a refund."
          values={{
            a: (...chunks) => <a href="https://support.apple.com/en-us/HT204084">{chunks}</a>,
          }}
        />
      </p>
    </>,
  ],
  [
    <T id="faq.deleteAccountQ" defaultMessage="How do I delete my account?" />,
    <p>
      <T
        id="faq.deleteAccountA"
        defaultMessage="Please contact our customer support department at <a>info@ridewithgps.com</a> and we will be glad to assist you."
        values={{
          a: (...chunks) => <a href="mailto:info@ridewithgps.com">{chunks}</a>,
        }}
      />
    </p>,
  ],
  [
    <T id="faq.stopRecordingQ" defaultMessage="How do I stop a ride recording on the Ride with GPS app?" />,
    <>
      <p>
        <T
          id="faq.stopRecordingA"
          defaultMessage="On the app homescreen tap <b>Go Ride</b>. When you are ready to stop recording, press and hold the <b>Pause</b> button for 2 seconds and select <b>Finish Ride</b>."
          values={{
            b: (...chunks) => <b>{chunks}</b>,
          }}
        />
      </p>
    </>,
  ],
  [
    <T id="faq.uploadFileQ" defaultMessage="How do I upload GPX, TCX, or FIT files to my account?" />,
    <p>
      <T
        id="faq.uploadFileA"
        defaultMessage="We support uploads from our website <a>ridewithgps.com</a>. When logged in to the website, click <b>Upload</b>, then click <b>Select Files</b> to choose ride and route files from your computer and they will be uploaded to your library."
        values={{
          a: (...chunks) => <a href="/">{chunks}</a>,
          b: (...chunks) => <b>{chunks}</b>,
        }}
      />
    </p>,
  ],
];

export const Faq = () => {
  return (
    <StandardLayout greyBg>
      <div className={cx(interfaceBase, styles.Faq)}>
        <ColWrap>
          <MainCol>
            <Card>
              <h1>
                <T id="faq.frequentlyAskedQuestions" defaultMessage="Frequently Asked Questions" />
              </h1>
              <p>
                <T id="faq.subheader" defaultMessage="We’re here to help you get the most out of Ride with GPS." />
              </p>
              <hr />
              <FAQAccordian>
                {faqItems.map(([question, answer], i) => (
                  <FAQAccordianItem key={i} question={question} answer={answer} />
                ))}
              </FAQAccordian>
            </Card>
          </MainCol>

          <SecondaryCol>
            <h1 data-heading-alt>
              <T id="faq.helpCenter" defaultMessage="Help center" />
            </h1>
            <Card>
              <img src={helpImg} srcSet={`${helpImg} 1x, ${helpImg2x} 2x, ${helpImg3x} 3x`} />
              <p>
                <T
                  id="faq.helpDetails"
                  defaultMessage="We have an extensive library of help articles to assist you in getting the most out of Ride with GPS."
                />
              </p>
              <CTA margins={false} href="/help">
                <T id="faq.helpLink" defaultMessage="Visit Help Center" />
              </CTA>
            </Card>
            <h1 data-heading-alt>
              <Msg m={messages.contactUs} />
            </h1>
            <Card>
              <p>
                <T
                  id="faq.contactUsDetails"
                  defaultMessage="Our customer support department is available seven days a week, providing support to users in English. To reach us, send an email and we will get back to you as soon as possible."
                />
              </p>
              <a className={styles.contactUsLink} href="mailto:info@ridewithgps.com">
                <Msg m={messages.contactUs} />
              </a>
            </Card>
          </SecondaryCol>
        </ColWrap>
      </div>
    </StandardLayout>
  );
};

const FAQAccordian = ({ children }) => {
  const [expanded, setExpanded] = useState(Set());

  const toggle = (idx) => {
    setExpanded(expanded.has(idx) ? expanded.remove(idx) : expanded.add(idx));
  };

  return children.map((c, idx) => {
    return React.cloneElement(c, {
      idx,
      key: idx,
      expanded: expanded.has(idx),
      onClick: (idx) => toggle(idx),
    });
  });
};

const FAQAccordianItem = ({ question, answer, idx, expanded, onClick }) => {
  return (
    <div className={styles.FAQAccordianItem}>
      {expanded ? (
        <>
          <h2 onClick={() => onClick(idx)} className={styles.itemHeader}>
            <span>{question}</span> <Icon className={styles.icon} size={26} i="ios-remove-circle-outline" />
          </h2>
          <p>{answer}</p>
        </>
      ) : (
        <>
          <h3 onClick={() => onClick(idx)} className={styles.itemHeader}>
            <span>{question}</span> <Icon className={styles.icon} size={26} i="ios-add-circle-outline" />
          </h3>
        </>
      )}
      <hr />
    </div>
  );
};
